import type React from 'react';
import { useEffect, useState } from 'react';

import type { SocialSharing } from '@change/ds-core-ui';
import { Button, IMessageIcon, SocialIcon, Text } from '@change/ds-core-ui';
import { CheckIcon, LinkIcon, SignaturesIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { Translate, TranslateHtml, useI18n } from '@change-corgi/core-react/i18n';

import { SocialIconWithSuccessState } from '#src/app/shared/components/share';
import type { TranslatableLabel } from '#src/app/shared/hooks/i18n';
import type { ShareAction as ShareActionType } from '#src/app/shared/hooks/share';

/**
 * EXPERIMENTAL - Constituents-in-PSF "local signer" share screen.
 *
 * Presentational: it owns the experiment chrome + copy, and delegates the share
 * buttons to the control's real share components, fed `shareActions` configs:
 *   - the real `psf/share` page passes real configs (`useShareActions`), so the
 *     buttons actually share + track;
 *   - the hello sandbox passes mock configs (see LocalSignerShareExample).
 *
 * Two hypotheses (Figma "Updated share screen" + Confluence experiment table):
 *   - 'motivation' (H1): copy-link + a grid of share-target icons.
 *   - 'enablement' (H2): copy-link + a list of *local* channels + a small icon row.
 */

type SocialVariant = React.ComponentProps<typeof SocialSharing>['variant'];

/** A share action config - the shape `useShareActions` produces, trimmed to what this screen needs. */
export type LocalShareAction = Readonly<{
	ShareAction: ShareActionType['ShareAction'];
	channel: string;
	icon: React.ReactElement;
	label: TranslatableLabel;
	successLabel?: TranslatableLabel;
	variant?: SocialVariant;
	url: string;
}>;

// H2 local-channel list: which share channel backs each local row (Figma node 203:7816).
// `icon`/`variant` override the channel's default look where the Figma differs (SMS shows the
// green Messages icon here, but a grey bubble in the H1 grid).
const LOCAL_ROWS: ReadonlyArray<{
	channel: string;
	labelKey: string;
	icon?: React.ReactElement;
	variant?: SocialVariant;
}> = [
	{ channel: 'facebook', labelKey: 'channel_community' },
	{ channel: 'sms', labelKey: 'channel_neighbors', icon: <IMessageIcon />, variant: 'iMessage' },
	{ channel: 'instagram', labelKey: 'channel_city' },
	{ channel: 'whatsapp', labelKey: 'channel_groupchat' },
];

// H2 small icon row below the list.
const BOTTOM_ROW: readonly string[] = [
	'mobileNativeShare',
	'email',
	'facebookMessenger',
	'tiktok',
	'nextdoor',
	'reddit',
];

type ByChannel = (channel: string) => LocalShareAction | undefined;

const Headline: React.FC<
	Readonly<{
		target: string;
		isEnablement: boolean;
		isFirstLocalSigner: boolean;
		signerCount: number;
	}>
> = ({ target, isEnablement, isFirstLocalSigner, signerCount }) => {
	const { translatePlural } = useI18n();
	return (
		<div className={isEnablement ? 'text-left' : 'text-center'}>
			<Text as="h1" variant="heading" size="small" className="text-typography-strong">
				<TranslateHtml value="corgi.local_signer_share.headline_main" replacements={{ target }} />{' '}
				<Text as="span" variant="heading" size="small" className={cn({ 'text-typography-weak': isEnablement })}>
					<Translate value="corgi.local_signer_share.headline_more" />
				</Text>
			</Text>

			{/* H1 only: "You joined N other local signers" */}
			{!isEnablement && (
				<Text
					as="p"
					variant="body"
					size="small"
					fontWeight="bold"
					className="text-typography-weak mt-3 flex items-center justify-center gap-1.5"
				>
					<SignaturesIcon className="h-4 w-4" aria-hidden />
					{isFirstLocalSigner ? (
						<Translate value="corgi.local_signer_share.joined_first" />
					) : (
						translatePlural('corgi.local_signer_share.joined', signerCount, { count: signerCount })
					)}
				</Text>
			)}
		</div>
	);
};

// Matches the control share buttons (`useSocialIconWithSuccessState`): the copied/failed label shows for this long.
const COPY_RESULT_RESET_MS = 3000;

type CopyResult = 'success' | 'error';

function useCopyResultMessage(result: CopyResult | undefined): string | undefined {
	const { translate } = useI18n();
	if (result === 'success') return translate('corgi.local_signer_share.copy_success');
	if (result === 'error') return translate('corgi.local_signer_share.copy_error');
	return undefined;
}

/**
 * Copy link - reuses the real copy ShareAction behind the experiment's button styling. Like the control
 * share buttons, the label switches to a copied (or failed) message for a beat after the copy, and the
 * result is announced through a polite live region.
 */
const CopyLinkRow: React.FC<{ copy: LocalShareAction }> = ({ copy }) => {
	const { translate } = useI18n();
	const [result, setResult] = useState<CopyResult | undefined>(undefined);

	useEffect(() => {
		if (result === undefined) return undefined;
		const timeout = window.setTimeout(() => setResult(undefined), COPY_RESULT_RESET_MS);
		return () => window.clearTimeout(timeout);
	}, [result]);

	const resultMessage = useCopyResultMessage(result);

	return (
		<>
			<copy.ShareAction onAfterAction={(success) => setResult(success ? 'success' : 'error')}>
				<Button
					variant="primary"
					size="large"
					className="w-full"
					leadingIcon={result === 'success' ? <CheckIcon data-qa="copy-link-check" /> : <LinkIcon />}
				>
					{resultMessage ?? translate('corgi.local_signer_share.copy_link')}
				</Button>
			</copy.ShareAction>
			<output className="sr-only">{resultMessage ?? ''}</output>
		</>
	);
};

const LocalChannelList: React.FC<{ byChannel: ByChannel }> = ({ byChannel }) => (
	<div className="flex flex-col gap-2">
		{LOCAL_ROWS.map(({ channel, labelKey, icon, variant }) => {
			const action = byChannel(channel);
			if (!action) return null;
			return (
				<action.ShareAction key={channel}>
					<button
						type="button"
						className="border-stroke-weaker bg-background-base flex items-center gap-2 rounded-lg border px-4 py-4 text-left"
					>
						<SocialIcon size="small" variant={variant ?? action.variant} icon={icon ?? action.icon} />
						<Text as="span" variant="body" size="medium" fontWeight="bold" className="text-typography-strong">
							<Translate value={`corgi.local_signer_share.${labelKey}`} />
						</Text>
					</button>
				</action.ShareAction>
			);
		})}
	</div>
);

const IconGrid: React.FC<{ actions: readonly LocalShareAction[] }> = ({ actions }) => (
	// Icon grid - same 48px "Social Sharing Button" size across hypotheses (Figma 203:7840).
	<div className="grid grid-cols-4 justify-items-center gap-y-5">
		{actions.map((action) => (
			<SocialIconWithSuccessState
				key={action.channel}
				size="large"
				buttonConfig={{
					ShareAction: action.ShareAction,
					icon: action.icon,
					label: action.label,
					successLabel: action.successLabel,
					variant: action.variant,
				}}
			/>
		))}
	</div>
);

const ContinueFooter: React.FC<{ onContinue?: () => void }> = ({ onContinue }) => (
	<div className="border-stroke-weaker border-t px-6 py-4 text-center">
		<Button variant="tertiary" onClick={onContinue}>
			<span className="underline">
				<Translate value="corgi.local_signer_share.continue" />
			</span>
		</Button>
	</div>
);

type Props = {
	hypothesis?: 'motivation' | 'enablement';
	/** The petition's relevant location (city or state, by reach), shown as "[location] leaders". */
	location?: string;
	isFirstLocalSigner?: boolean;
	signerCount?: number;
	/** Share configs (copy + social channels). Real on the page, mock in the sandbox. */
	shareActions: readonly LocalShareAction[];
	onContinue?: () => void;
	/** Rendered inside a sheet that fills it edge to edge: square bottom, top corners matching the sheet's. */
	inSheet?: boolean;
};

export const LocalSignerShareComponent: React.FC<Props> = ({
	hypothesis = 'motivation',
	location,
	isFirstLocalSigner = false,
	signerCount = 48,
	shareActions,
	onContinue,
	inSheet = false,
}) => {
	const { translate } = useI18n();
	const target = location
		? translate('corgi.local_signer_share.location_leaders', { location })
		: translate('corgi.local_signer_share.decision_makers');
	const isEnablement = hypothesis === 'enablement';

	const byChannel: ByChannel = (channel) => shareActions.find((a) => a.channel === channel);
	const copy = byChannel('copy');
	const socials = shareActions.filter((a) => a.channel !== 'copy');
	const bottomRowActions = BOTTOM_ROW.map(byChannel).filter((a): a is LocalShareAction => Boolean(a));

	return (
		<div
			className={cn(
				'bg-background-base relative flex min-h-[calc(100svh-23rem)] w-full flex-col overflow-hidden',
				inSheet ? 'rounded-t-lg' : 'rounded-2xl',
			)}
			data-qa="local-signer-share"
			style={{
				// Brand tint fading down into the card's base colour (a vertical transparent->base layer over
				// the DS `gradient-background-light-2` tint, which has its own dark-mode value, no mask / no z-index tricks so it renders reliably across browsers).
				backgroundImage:
					'linear-gradient(to bottom, transparent 0%, transparent 65%, var(--color-background-base) 100%), var(--gradient-background-light-2)',
			}}
		>
			<div className="flex flex-1 flex-col gap-5 px-6 pt-12 pb-4">
				<Headline
					target={target}
					isEnablement={isEnablement}
					isFirstLocalSigner={isFirstLocalSigner}
					signerCount={signerCount}
				/>

				{copy && <CopyLinkRow copy={copy} />}

				{isEnablement ? (
					<>
						<Text as="p" variant="body" size="medium" className="text-typography-weak">
							<Translate value="corgi.local_signer_share.local_label" />
						</Text>
						<LocalChannelList byChannel={byChannel} />
						<IconGrid actions={bottomRowActions} />
					</>
				) : (
					<IconGrid actions={socials} />
				)}
			</div>

			<ContinueFooter onContinue={onContinue} />
		</div>
	);
};
